import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Ansprechpartner, AnsprechpartnerMitHerkunft, Nachfolge,
              RolleAuswahl } from "@kundeninfo/api-client";
import { BearbeitenLeiste, Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload,
         useFormularZustand, useRecordLock, useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import type { SperrDienst } from "@kundeninfo/ui";
import { formatDatum } from "../format.js";
import { KiKnopf } from "../components/meHuellen.js";
import type { VerlassenWaechter } from "../router.js";
import { AdressListe } from "./AdressListe.js";
import type { AdressClient } from "./AdressListe.js";
import "./KundeAnsprechpartner.css";

/**
 * Nur die Klientenmethoden, die dieser Tab wirklich braucht — so lässt er sich im Test
 * mit einer Handvoll vi.fn zeichnen, ohne den ganzen ApiClient nachzubauen (Muster aus
 * KundeStammdaten.tsx).
 *
 * Seit Aufgabe 9 kommen die fünf Adressmethoden aus `AdressClient` dazu — der Tab reicht
 * denselben Klienten unverändert an den Baustein `AdressListe` weiter (Besitzer
 * `contacts`), er braucht sie sonst nirgends selbst. `ApiClient` erfüllt die Erweiterung
 * bereits; nur der `Pick`-Typ hier wächst.
 */
export type AnsprechpartnerClient = Pick<ApiClient,
  "listContacts" | "listSelectableContactRoles" | "createContact" | "updateContact"
  | "setMainContact" | "markContactLeft" | "acquireLock" | "refreshLock" | "releaseLock">
  & AdressClient;

/** Die Verdrahtung, die von App über KundenAkte hierher durchgereicht wird. */
export type AnsprechpartnerUmfeld = {
  client: AnsprechpartnerClient;
  /** Ob die Übernahme einer fremden Sperre angeboten wird — LEAD/ADMIN, entschieden vom
   *  Aufrufer (siehe darfSperreUebernehmen in session.ts). */
  darfUebernehmen: boolean;
  registriereVerlassenWaechter(frage: VerlassenWaechter): () => void;
  /**
   * Sprung in die Akte des Ursprungskunden eines GEERBTEN Ansprechpartners (Etappe 2).
   * Ohne Angabe steht die Herkunft als Text da — der Reiter ist dann eingebaut, wo es
   * keine Kette gibt.
   */
  onKunde?: (id: string) => void;
};

export type KundeAnsprechpartnerProps = AnsprechpartnerUmfeld & { kundeId: string };

/**
 * Die vier Wörter, die die Oberfläche für `gender` zeigt — deutsch, während der Schlüssel
 * englisch bleibt (Hausregel „Datenmodell englisch, Oberfläche deutsch"). Die Tabelle
 * steht HIER und nicht in der Domäne: `web` importiert nichts aus `domain`, und eine
 * Beschriftung ist ohnehin keine Regel.
 */
const ANREDEN: { wert: string; text: string }[] = [
  { wert: "MALE", text: "männlich" },
  { wert: "FEMALE", text: "weiblich" },
  { wert: "DIVERSE", text: "divers" },
  { wert: "UNKNOWN", text: "unbekannt" },
];

/** Die Formularwerte. Die Angaben sind Zeichenketten, damit ein geleertes Feld erst beim
 *  Absenden zu null wird (der Schreibweg leert die Spalte dann wirklich). */
type Entwurf = {
  gender: string; title: string; firstName: string; lastName: string;
  jobTitle: string; email: string; phone: string; roleIds: string[];
};

type AngabenName = "title" | "firstName" | "lastName" | "jobTitle" | "email" | "phone";
/** Alle Feldnamen, die eine 422 dieser Masken nennen kann. */
type FeldName = AngabenName | "gender" | "roleIds" | "leftOn" | "nachfolger";

const ANGABEN: { name: AngabenName; label: string; typ?: "email" | "tel" }[] = [
  { name: "title", label: "Titel" },
  { name: "firstName", label: "Vorname" },
  { name: "lastName", label: "Nachname" },
  { name: "jobTitle", label: "Funktion" },
  { name: "email", label: "E-Mail", typ: "email" },
  { name: "phone", label: "Telefon", typ: "tel" },
];

const LEERER_ENTWURF: Entwurf = {
  // „unbekannt" ist die ehrliche Vorgabe: Wer nichts gewählt hat, hat nichts gesagt — und
  // der Beleg grüsst dann mit „Guten Tag …" statt mit einem geratenen „Herr".
  gender: "UNKNOWN", title: "", firstName: "", lastName: "",
  jobTitle: "", email: "", phone: "", roleIds: [],
};

function entwurfAus(k: Ansprechpartner): Entwurf {
  return {
    gender: k.gender,
    title: k.title ?? "", firstName: k.firstName ?? "", lastName: k.lastName,
    jobTitle: k.jobTitle ?? "", email: k.email ?? "", phone: k.phone ?? "",
    roleIds: [...k.roleIds],
  };
}

function entwurfGleich(a: Entwurf, b: Entwurf): boolean {
  return a.gender === b.gender
    && ANGABEN.every((f) => a[f.name] === b[f.name])
    && a.roleIds.length === b.roleIds.length
    && a.roleIds.every((id) => b.roleIds.includes(id));
}

function oderNull(wert: string): string | null {
  return wert.trim() === "" ? null : wert;
}

/**
 * Der Rumpf für Anlegen und Ändern — beide Wege ersetzen die Rollen VOLLSTÄNDIG, deshalb
 * gehen die Kennungen immer mit (Vertrag: AENDERN_SCHEMA in routes/contacts.ts).
 *
 * Migration 025: Die Ansprechpartner-Eingabe führt `gender`/`title`/`firstName`/
 * `lastName`; `anzeigename`/`briefanrede` sind Ableitungen des Servers, kein Eingabefeld.
 * KEIN `name`: Der Server leitet ihn ab und weist ein mitgeschicktes Feld mit 400 ab
 * (KONTAKT_SCHEMA, additionalProperties: false).
 */
function eingabeAus(entwurf: Entwurf): { contact: {
  gender: "MALE" | "FEMALE" | "DIVERSE" | "UNKNOWN";
  title: string | null; firstName: string | null; lastName: string;
  jobTitle: string | null; email: string | null; phone: string | null;
}; roleIds: string[] } {
  return {
    contact: {
      gender: entwurf.gender as "MALE" | "FEMALE" | "DIVERSE" | "UNKNOWN",
      title: oderNull(entwurf.title),
      firstName: oderNull(entwurf.firstName),
      lastName: entwurf.lastName,
      jobTitle: oderNull(entwurf.jobTitle),
      email: oderNull(entwurf.email),
      phone: oderNull(entwurf.phone),
    },
    roleIds: entwurf.roleIds,
  };
}

/**
 * Der heutige Kalendertag als "JJJJ-MM-TT" aus der ÖRTLICHEN Zeit. `toISOString()` wäre
 * hier falsch: Es rechnet nach UTC um, und östlich von Greenwich stünde am späten Abend
 * der Vortag im Feld (Kehrseite des Problems, das formatDatum in format.ts beschreibt).
 */
function heuteAlsTag(jetzt: Date = new Date()): string {
  const zwei = (n: number): string => String(n).padStart(2, "0");
  return `${jetzt.getFullYear()}-${zwei(jetzt.getMonth() + 1)}-${zwei(jetzt.getDate())}`;
}

/**
 * `conflict.current` kommt als unknown über die Leitung (die API kann seine Gestalt im
 * Schema nicht versprechen, siehe openapi.ts). Tatsächlich liefert der Fehlerhandler den
 * vollen Ansprechpartner; hier wird das geprüft statt blind behauptet.
 */
function alsKontakt(wert: unknown): Ansprechpartner | null {
  if (typeof wert !== "object" || wert === null) return null;
  const k = wert as Partial<Ansprechpartner>;
  if (typeof k.id !== "string" || typeof k.anzeigename !== "string"
    || typeof k.version !== "number") return null;
  return wert as Ansprechpartner;
}

const KONFLIKT_ZEIT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
});

function formatKonfliktZeit(iso: string): string {
  const zeitpunkt = new Date(iso);
  if (Number.isNaN(zeitpunkt.getTime())) return iso;
  return `${KONFLIKT_ZEIT.format(zeitpunkt)} Uhr`;
}

type Konflikt = { von: string; wann: string; fremd: Ansprechpartner | null };

/** Der Ausscheiden-Dialog. `wahl` steht bei einem Hauptansprechpartner zunächst auf
 *  "OFFEN" — genau das ist der Zwang: ohne Entscheidung wird nicht abgeschickt. */
type AusscheidenStand = {
  kontakt: Ansprechpartner;
  leftOn: string;
  wahl: "OFFEN" | "NACHFOLGER" | "OHNE";
  nachfolgerId: string;
};

/**
 * Ein Ansprechpartner ist erst dann zum Ausscheiden bereit, wenn die Nachfolgefrage
 * beantwortet ist. Für alle anderen gibt es nichts zu entscheiden.
 *
 * Diese eine Stelle beantwortet die Frage für den Knopf (`disabled`) UND für das
 * Abschicken selbst — zwei getrennte Bedingungen liefen irgendwann auseinander, und dann
 * schickte ein Tastendruck ab, was der Knopf noch verweigert.
 */
function entscheidungGetroffen(d: AusscheidenStand): boolean {
  if (!d.kontakt.istHauptansprechpartner) return true;
  if (d.wahl === "OHNE") return true;
  return d.wahl === "NACHFOLGER" && d.nachfolgerId !== "";
}

/**
 * Der Inhalt des Tabs „Ansprechpartner".
 *
 * Alles geschieht IM Tab: Kundenkopf und Tableiste bleiben stehen, das Anlegen und das
 * Bearbeiten öffnen sich als strikte Modalpanels über der Liste („Der Kunde ist eine Ebene,
 * kein Ziel" — die Ebene wird nicht verlassen).
 *
 * Das Bearbeiten folgt dem Muster aus KundeStammdaten.tsx: „Bearbeiten" meldet den Wunsch
 * bei useRecordLock an (`entityType: "contact"` — die Person wird gesperrt, nicht der ganze
 * Kunde), und erst wenn die Sperre GEHALTEN ist, öffnen sich die Felder. Gegen verlorene
 * Änderungen schützt der Versionsvergleich beim Speichern, nicht die Sperre
 * (Entscheidung AA) — deshalb der 409-Kasten mit beiden Auflösungswegen.
 *
 * **Wo die Masken stehen** (Hausregel „striktes Modalpanel", Entscheid Dirk vom
 * 06.09.2026): Anlegen und Bearbeiten laufen im Panel aus @kundeninfo/ui, mit genau zwei
 * Ausgängen in seiner Fußleiste. Die SPERRE wandert dabei nicht mit — sie hängt weiterhin
 * an `wunsch`. Deshalb steht die Leiste an ZWEI Orten und nie zugleich: an der ZEILE,
 * solange auf eine fremde Sperre gewartet wird (Band, „Übernehmen", „Nicht mehr warten" —
 * dort steht kein Panel), und IM Panel, sobald bearbeitet wird (dann nur noch mit dem
 * Störungsband, `aktionenImPanel`).
 *
 * Nach JEDEM erfolgreichen Schreibweg wird die Liste neu geladen, nicht nur der betroffene
 * Satz gepflegt: Ein Hauptansprechpartner-Wechsel ändert immer ZWEI Zeilen (der eine
 * bekommt den Stern, der andere verliert ihn), und der erste angelegte Ansprechpartner
 * wird serverseitig zugleich Hauptansprechpartner (Entscheidung AI). Wer hier nur den
 * eigenen Satz nachzieht, zeigt danach zwei Sterne oder keinen.
 */
export function KundeAnsprechpartner(p: KundeAnsprechpartnerProps): ReactElement {
  const [kontakte, setKontakte] = useState<AnsprechpartnerMitHerkunft[] | null>(null);
  const [rollen, setRollen] = useState<RolleAuswahl[]>([]);
  const [rollenFehler, setRollenFehler] = useState<string | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  /** Die Anlege-Maske ist offen. */
  const [neuOffen, setNeuOffen] = useState(false);
  /** Der Bearbeitungswunsch — treibt useRecordLock und bleibt auch bei FREMD bestehen,
   *  damit die Beobachtung weiterläuft (siehe KundeStammdaten.tsx). */
  const [wunsch, setWunsch] = useState<string | null>(null);
  /** Der Satz, auf dem der Entwurf beruht — Quelle der Version beim Speichern. */
  const [stand, setStand] = useState<Ansprechpartner | null>(null);
  /** Die Felder der Bearbeitung sind offen (erst wenn die Sperre GEHALTEN ist). */
  const [bearbeitet, setBearbeitet] = useState<string | null>(null);

  const [entwurf, setEntwurf] = useState<Entwurf>(LEERER_ENTWURF);
  const [ausgang, setAusgang] = useState<Entwurf>(LEERER_ENTWURF);
  const [konflikt, setKonflikt] = useState<Konflikt | null>(null);
  /** „Meine Eingaben behalten“: nur die Versionsbasis vorrücken (siehe KundeStammdaten). */
  const eingabenBehalten = useRef(false);

  const [sternFrage, setSternFrage] = useState<Ansprechpartner | null>(null);
  const [ausscheiden, setAusscheiden] = useState<AusscheidenStand | null>(null);
  const [arbeitet, setArbeitet] = useState(false);

  /**
   * Welcher Ansprechpartner seine Kontaktwege offen hat — die KENNUNG, nicht ein
   * Wahrheitswert je Zeile (Aufgabe 9).
   *
   * Eine Kennung kann nur EINEN Abschnitt offen halten, und genau das ist gewollt: Zwei
   * offene Listen nebeneinander wären zwei Ladevorgänge, zwei Panels und zwei Sätze
   * Knöpfe, deren zugängliche Namen sich gleichen (Hausregel „Dialog-Guards auf
   * Objekt-Id"). Sie fällt beim Aktenwechsel mit dem übrigen Zustand weg — der
   * Lade-Effekt setzt sie auf null.
   */
  const [wegeFuer, setWegeFuer] = useState<string | null>(null);
  /**
   * Zählt hoch, sobald die STAMMDATEN (E-Mail/Telefon) eines Ansprechpartners
   * geschrieben wurden. Diese schreiben serverseitig auf die Hauptadresse durch — die
   * Kontaktwege-Liste kennt das aber nicht von selbst, sie lädt nur bei ihrem eigenen
   * `key`-Wechsel neu. Der Zähler steht in genau diesem `key` (siehe unten), damit ein
   * offener Abschnitt nach dem Speichern wirklich neu holt, statt seinen alten Bestand zu
   * zeigen.
   */
  const [wegeVersion, setWegeVersion] = useState(0);

  const { kundeId, client, onKunde } = p;
  const listContacts = client.listContacts;
  const listRollen = client.listSelectableContactRoles;

  /**
   * Formularzustände der Feldmasken — Anlegen und Bearbeiten führen denselben Entwurf, es
   * steht immer nur eine offen (ME-Etappe 3, Aufgabe 3). `laeuft` sperrt Felder und Fuß;
   * eine 422 hängt am Feld (Rollen: an den Kästchen) und der Fokus springt dorthin; eine
   * 409 beim Bearbeiten hält die Eingaben als „fremder Stand“ fest: „Neu laden“ übernimmt
   * den Serverstand, „Meine Eingaben behalten“ nur dessen Version.
   */
  const kontaktFormular = useFormularZustand<true>({
    speichern: async () => {
      if (neuOffen) {
        await client.createContact(kundeId, eingabeAus(entwurf));
        return true;
      }
      if (stand === null) throw new Error("Kein Ansprechpartner offen.");
      try {
        await client.updateContact(stand.id, stand.version, eingabeAus(entwurf));
      } catch (fehler) {
        if (fehler instanceof ApiError && fehler.status === 409) {
          setKonflikt(fehler.conflict !== undefined
            ? { von: fehler.conflict.changedBy, wann: fehler.conflict.changedAt,
                fremd: alsKontakt(fehler.conflict.current) }
            : { von: "", wann: "", fremd: null });
        }
        throw fehler;
      }
      return true;
    },
    neuLaden: async () => {
      const behalten = eingabenBehalten.current;
      eingabenBehalten.current = false;
      if (stand === null) return;
      let fremd = konflikt?.fremd ?? null;
      if (fremd === null) {
        const antwort = await listContacts(kundeId);
        setKontakte(antwort.items);
        fremd = antwort.items.find((k) => k.id === stand.id) ?? null;
      }
      if (fremd !== null) {
        setStand(fremd);
        if (!behalten) {
          setEntwurf(entwurfAus(fremd));
          setAusgang(entwurfAus(fremd));
        }
      }
      setKonflikt(null);
    },
    bekannteFelder: ["gender", "title", "firstName", "lastName", "jobTitle", "email", "phone",
      "roleIds"] satisfies FeldName[],
  });
  const speichert = kontaktFormular.laeuft;

  /** Formularzustände der Ausscheide-Maske. Eine 409 (der Satz hat eine Version) lädt mit
   *  „Neu laden“ den frischen Satz; Datum und Wahl bleiben stehen — sie stammen nicht vom
   *  Server. */
  const ausscheidenFormular = useFormularZustand<true>({
    speichern: async () => {
      const d = ausscheiden;
      if (d === null) throw new Error("Keine Ausscheide-Maske offen.");
      const nachfolge: Nachfolge | null = d.kontakt.istHauptansprechpartner
        ? { contactId: d.wahl === "NACHFOLGER" ? d.nachfolgerId : null }
        : null;
      await client.markContactLeft(d.kontakt.id, d.kontakt.version, d.leftOn, nachfolge);
      return true;
    },
    neuLaden: async () => {
      const antwort = await listContacts(kundeId);
      setKontakte(antwort.items);
      setAusscheiden((d) => {
        if (d === null) return d;
        const frisch = antwort.items.find((k) => k.id === d.kontakt.id);
        return frisch === undefined ? d : { ...d, kontakt: frisch };
      });
    },
    bekannteFelder: ["leftOn", "nachfolger"] satisfies FeldName[],
  });
  const feldFehler = (ausscheiden !== null ? ausscheidenFormular.feldFehler
    : kontaktFormular.feldFehler) as Partial<Record<FeldName, string>>;

  /** Neu laden nach jedem Schreibweg. Wirft nicht — ein Fehler wird angezeigt. */
  async function neuLaden(): Promise<void> {
    try {
      const antwort = await listContacts(kundeId);
      setKontakte(antwort.items);
      setLadeFehler(null);
    } catch (fehler) {
      setLadeFehler(fehler instanceof ApiError ? fehler.message
        : "Die Ansprechpartner konnten nicht geladen werden.");
    }
  }

  useEffect(() => {
    let abgebrochen = false;
    setKontakte(null);
    setLadeFehler(null);
    // Kein Zustand überlebt den Kontextwechsel: Die Kontaktwege eines Ansprechpartners
    // bleiben nicht offen, wenn die Kundenakte wechselt.
    setWegeFuer(null);
    setWegeVersion(0);
    listContacts(kundeId)
      .then((antwort) => { if (!abgebrochen) setKontakte(antwort.items); })
      .catch((fehler: unknown) => {
        if (abgebrochen) return;
        setLadeFehler(fehler instanceof ApiError ? fehler.message
          : "Die Ansprechpartner konnten nicht geladen werden.");
      });
    return () => { abgebrochen = true; };
  }, [kundeId, listContacts]);

  // Die Rollen hängen am Kunden nicht — sie werden einmal geholt. Scheitert das, bleibt
  // der Tab bedienbar (Liste, Stern, Ausscheiden) und nur das Ankreuzen entfällt; ein
  // Hinweis sagt es, statt die Rollen still als "keine" auszugeben.
  useEffect(() => {
    let abgebrochen = false;
    listRollen()
      .then((antwort) => { if (!abgebrochen) setRollen(antwort.items); })
      .catch(() => {
        if (!abgebrochen) {
          setRollenFehler("Die Rollen konnten nicht geladen werden — Rollen lassen sich "
            + "gerade nicht zuordnen.");
        }
      });
    return () => { abgebrochen = true; };
  }, [listRollen]);

  // Der Adapter auf den Client; die Fehlerform der 423 ist bereits deckungsgleich mit dem,
  // was useRecordLock erwartet (siehe KundeStammdaten.tsx).
  const dienst: SperrDienst = {
    acquire: (ziel, force) => client.acquireLock(ziel, force),
    refresh: (ziel) => client.refreshLock(ziel),
    release: (ziel) => client.releaseLock(ziel),
  };
  const { zustand, uebernehmen } = useRecordLock({
    dienst,
    ziel: wunsch === null ? null : { entityType: "contact", entityId: wunsch },
    aktiv: wunsch !== null,
  });

  // Sobald die Sperre hält, öffnen sich die Felder — beim direkten Erwerb wie nach einer
  // Übernahme oder wenn die Beobachtung eine gefallene fremde Sperre ergattert.
  const sperrArt = zustand.art;
  useEffect(() => {
    if (wunsch !== null && sperrArt === "GEHALTEN") setBearbeitet(wunsch);
  }, [wunsch, sperrArt]);

  // Scheitert schon der Erwerb technisch (nicht 423), wäre die Leiste stumm: ihr
  // FEHLER-Band zeigt sie nur während der Bearbeitung. Deshalb hier die Meldung und zurück
  // in den Ruhezustand — ein erneuter Klick auf „Bearbeiten" versucht es wieder.
  useEffect(() => {
    if (wunsch !== null && bearbeitet === null && zustand.art === "FEHLER") {
      setMeldung(zustand.meldung);
      setWunsch(null);
    }
  }, [wunsch, bearbeitet, zustand]);

  const formularOffen = neuOffen || bearbeitet !== null;
  const geaendert = formularOffen && !entwurfGleich(entwurf, ausgang);

  /*
   * Verlassen-Wächter, solange ein Formular offen steht.
   *
   * Bewusst ANDERS als in KundeStammdaten.tsx, wo erst eine Änderung ihn anmeldet: Ein
   * offenes Formular hält hier zusätzlich eine Sperre auf einer Person; die Navigation
   * weg von der Akte lässt beides zurück. Damit der Wächter trotzdem nicht lügt, FRAGT er
   * nur bei wirklich geänderten Eingaben — sonst gibt er die Navigation stumm frei.
   * (Die Antwort steht in einer Ref, damit das Ändern einer Eingabe den Wächter nicht bei
   * jedem Tastendruck ab- und wieder anmeldet.)
   */
  const geaendertRef = useRef(geaendert);
  geaendertRef.current = geaendert;
  const { bestaetigen, bestaetigung } = useBestaetigung();
  const registriere = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!formularOffen) return;
    return registriere(() => !geaendertRef.current || bestaetigen({
      titel: "Seite verlassen?",
      text: "Es gibt ungespeicherte Änderungen. Sie gehen verloren.",
      bestaetigenText: "Verlassen",
      gefaehrlich: true,
    }));
  }, [formularOffen, registriere]);

  // …und derselbe geänderte Stand für das ECHTE Verlassen (Tab zu, neu laden,
  // App-Wechsel), das der Wächter oben nicht sieht (2a-Kleinpunkt). Hier zählt allein
  // `geaendert`: Ein offenes, aber unberührtes Formular soll den Browserdialog nicht
  // auslösen — genau die Unterscheidung, die der Wächter oben über `geaendertRef` trifft.
  useBeforeUnload(geaendert);

  /**
   * 2a-Kleinpunkt W1: Rückzug aus dem Wartezustand. Dieselbe Mechanik wie in
   * KundeStammdaten.tsx — der Wunsch wird zurückgenommen, useRecordLock sieht
   * `aktiv:false` und beendet seine FREMD-Beobachtung; die Zeile fällt in ihren
   * Lesezustand mit den Aktionsknöpfen zurück. Der Rückbau ist derselbe wie beim
   * Schliessen des Formulars: Im Wartezustand steht nichts Eingegebenes offen.
   */
  function beiNichtMehrWarten(): void {
    formularSchliessen();
  }

  function formularSchliessen(): void {
    setNeuOffen(false);
    setBearbeitet(null);
    setStand(null);
    setWunsch(null); // gibt die Sperre über useRecordLock frei
    setEntwurf(LEERER_ENTWURF);
    setAusgang(LEERER_ENTWURF);
    kontaktFormular.zuruecksetzen();
    setKonflikt(null);
  }

  /**
   * Der EINE Ausgang der Feldmasken neben dem Speichern (Hausregel „striktes Modalpanel",
   * 06.09.2026): kein Ausgang unter laufendem Schreiben, Rückfrage bei ungespeicherten
   * Eingaben. Knopf und Escape gehen durch diese Funktion — beim Anlegen wie beim
   * Bearbeiten, denn beide führen denselben Entwurf.
   *
   * Beim Bearbeiten hängt daran mehr als die Eingabe: `formularSchliessen` gibt die SPERRE
   * frei. Ein Abbrechen, das sie stehen liesse, sperrte die Person für alle anderen aus.
   */
  async function formularAbbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(geaendert))) return;
    formularSchliessen();
  }

  /**
   * Der Ausgang aus der Ausscheide-Maske (Hausregel „striktes Modalpanel", 06.09.2026):
   * kein Ausgang unter laufendem Schreiben, Rückfrage, sobald etwas darin steht.
   *
   * „Etwas darin" heisst hier: ein anderes Datum als heute oder eine getroffene Wahl — der
   * Stand beim Öffnen ist genau `{ leftOn: heute, wahl: "OFFEN", nachfolgerId: "" }` (siehe
   * den Ausscheiden-Knopf an der Zeile), er wird deshalb nicht zusätzlich mitgeführt.
   */
  async function ausscheidenAbbrechen(): Promise<void> {
    if (ausscheidenFormular.laeuft) return;
    const d = ausscheiden;
    const beruehrt = d !== null
      && (d.leftOn !== heuteAlsTag() || d.wahl !== "OFFEN" || d.nachfolgerId !== "");
    if (!(await darfAbbrechen(beruehrt))) return;
    setAusscheiden(null);
  }

  function beiNeu(): void {
    setEntwurf(LEERER_ENTWURF);
    setAusgang(LEERER_ENTWURF);
    kontaktFormular.zuruecksetzen();
    setMeldung(null);
    setKonflikt(null);
    setNeuOffen(true);
  }

  function beiBearbeiten(k: Ansprechpartner): void {
    setEntwurf(entwurfAus(k));
    setAusgang(entwurfAus(k));
    setStand(k);
    kontaktFormular.zuruecksetzen();
    setMeldung(null);
    setKonflikt(null);
    setWunsch(k.id);
  }

  async function beiAnlegen(): Promise<void> {
    if (await kontaktFormular.aktion() !== true) return;
    formularSchliessen();
    // Der erste Ansprechpartner wird serverseitig zugleich Hauptansprechpartner
    // (Entscheidung AI) — der Stern steht deshalb nach diesem Laden da, ohne dass
    // jemand ihn setzen musste.
    await neuLaden();
    // E-Mail/Telefon schreiben serverseitig auf die Hauptadresse durch — eine offene
    // Kontaktwege-Liste (nicht dieser Person, die gibt es ja gerade erst) lädt darüber
    // neu, falls sie an derselben Version hängt.
    setWegeVersion((v) => v + 1);
  }

  async function beiSpeichern(): Promise<void> {
    if (stand === null) return;
    if (await kontaktFormular.aktion() !== true) return;
    formularSchliessen();
    await neuLaden();
    // E-Mail/Telefon schreiben serverseitig auf die Hauptadresse durch (Aufgabe 4/9) —
    // eine offene Kontaktwege-Liste hängt an genau diesem Zähler in ihrem `key` und lädt
    // deshalb neu, statt ihren alten Bestand zu zeigen.
    setWegeVersion((v) => v + 1);
  }

  /** Konfliktauflösung 2: die eigene Eingabe behalten. Die Versionsbasis rückt auf den
   *  Serverstand vor — das nächste Speichern überschreibt den fremden Stand dann BEWUSST
   *  (der Hinweis hat beide Stände genannt). Auflösung 1 ist „Neu laden“ des Formulars. */
  function eigeneEingabenBehalten(): void {
    eingabenBehalten.current = true;
    void kontaktFormular.neuLaden();
  }

  async function sternUmhaengen(): Promise<void> {
    if (sternFrage === null) return;
    setArbeitet(true);
    setMeldung(null);
    try {
      await client.setMainContact(kundeId, sternFrage.id);
      setSternFrage(null);
      await neuLaden();
    } catch (fehler) {
      setMeldung(fehler instanceof ApiError ? fehler.message
        : "Der Hauptansprechpartner konnte nicht gewechselt werden.");
      setSternFrage(null);
    } finally {
      setArbeitet(false);
    }
  }

  async function ausscheidenAbschicken(): Promise<void> {
    const d = ausscheiden;
    if (d === null) return;
    // Zweiter Riegel neben dem stillgelegten Knopf: Ohne beantwortete Nachfolgefrage geht
    // nichts hinaus. Der Server verlangt sie ohnehin (422 NACHFOLGER_ERFORDERLICH) — die
    // Maske stellt sie, statt den Benutzer in die Ablehnung laufen zu lassen.
    if (!entscheidungGetroffen(d)) return;
    setMeldung(null);
    if (await ausscheidenFormular.aktion() !== true) return;
    setAusscheiden(null);
    await neuLaden();
  }

  /** Die Rollen zum Ankreuzen: aktive plus jede bereits zugeordnete stillgelegte — sonst
   *  fiele eine stillgelegte Zuordnung beim nächsten Speichern still weg. */
  const waehlbareRollen = rollen.filter(
    (r) => r.active || entwurf.roleIds.includes(r.id));

  function rolleUmschalten(id: string): void {
    setEntwurf((e) => ({
      ...e,
      roleIds: e.roleIds.includes(id)
        ? e.roleIds.filter((r) => r !== id)
        : [...e.roleIds, id],
    }));
  }

  /**
   * Die Felder von Anlege- und Bearbeitungsmaske — dieselben Eingaben, dieselbe
   * Fehlerzuordnung; nur der Rahmen darum unterscheidet sich.
   *
   * Bewusst eine Funktion, die JSX ZURÜCKGIBT, und keine eigene Komponente: Eine
   * innerhalb des Renders definierte Komponente bekommt bei jedem Neuzeichnen eine neue
   * Identität — React hängt ihren Teilbaum dann ab und neu auf, und der Fokus springt
   * nach jedem getippten Zeichen aus dem Feld.
   */
  function felderBlock(praefix: string): ReactElement {
    return (
      <>
        <div className="kap__felder">
          <Auswahl id={`${praefix}-gender`} name={`${praefix}-gender`} beschriftung="Anrede"
            wert={entwurf.gender}
            {...(feldFehler.gender !== undefined ? { fehler: feldFehler.gender } : {})}
            onGeaendert={(d) => setEntwurf((e) => ({ ...e, gender: d.wert }))}>
            {ANREDEN.map((a) => (
              <option key={a.wert} value={a.wert}>{a.text}</option>
            ))}
          </Auswahl>
          {ANGABEN.map((f) => {
            const fehlerText = feldFehler[f.name];
            return (
              <Feld
                key={f.name}
                id={`${praefix}-${f.name}`}
                label={f.label}
                wert={entwurf[f.name]}
                bearbeitbar
                onChange={(wert) => setEntwurf((e) => ({ ...e, [f.name]: wert }))}
                {...(f.typ !== undefined ? { typ: f.typ } : {})}
                {...(fehlerText !== undefined ? { fehler: fehlerText } : {})}
              />
            );
          })}
        </div>
        {/* Mehrfachauswahl: native Kästchen im Muster <fieldset><legend> (me-design hat
            dafür kein Element; Muster vscode-93). Ein Befund hängt per aria-invalid an den
            Kästchen und per aria-describedby an der Gruppe; dorthin springt der Fokus. */}
        <fieldset className="kap__rollenwahl"
          aria-describedby={feldFehler.roleIds !== undefined ? `${praefix}-rollen-fehler`
            : undefined}>
          <legend className="kap__rollenwahl-titel">Rollen</legend>
          {rollenFehler !== null && <Hinweis art="warnung">{rollenFehler}</Hinweis>}
          {rollenFehler === null && waehlbareRollen.length === 0 && (
            <p className="kap__hinweis">
              Es sind noch keine Rollen gepflegt — das geschieht in der Verwaltung.
            </p>
          )}
          {waehlbareRollen.map((r) => (
            <label key={r.id} className="kap__rollenwahl-eintrag">
              <input
                type="checkbox"
                checked={entwurf.roleIds.includes(r.id)}
                aria-invalid={feldFehler.roleIds !== undefined ? true : undefined}
                // Native Kästchen räumt der Haken nicht von selbst (nur ME-Felder, P8).
                onChange={() => { rolleUmschalten(r.id); kontaktFormular.feldFehlerRaeumen("roleIds"); }}
              />
              <span>{r.active ? r.name : `${r.name} (stillgelegt)`}</span>
            </label>
          ))}
          {feldFehler.roleIds !== undefined && (
            <span id={`${praefix}-rollen-fehler`} className="kap__feldfehler">
              {feldFehler.roleIds}
            </span>
          )}
        </fieldset>
      </>
    );
  }

  // Geerbte scheiden hier aus (Fixrunde 1, Ruling zu Aufgabe 6): Sie sind an DIESER Akte
  // weder Hauptansprechpartner noch Nachfolge-Kandidat — beides Rollen, die nur am
  // Ursprungskunden gelten. Ohne diesen Filter böte die Nachfolge-Auswahl unten
  // Kandidaten an, die der Server ablehnt, und ein an SEINEM Kunden hauptamtlicher
  // geerbter Ansprechpartner erschiene fälschlich als „bisheriger Hauptansprechpartner"
  // der Tochter.
  const aktive = kontakte?.filter((k) => k.status === "ACTIVE" && k.inherited !== true) ?? [];
  const bisherigerHaupt = aktive.find((k) => k.istHauptansprechpartner) ?? null;

  return (
    <div className="kap">
      <div className="kap__kopf">
        <h2 className="kap__titel">Ansprechpartner</h2>
        {/* me-knopf (ME-Etappe 4, P4). */}
        <KiKnopf art="primaer" onClick={beiNeu} deaktiviert={formularOffen || arbeitet}>
          Ansprechpartner hinzufügen
        </KiKnopf>
      </div>

      {/* Die Meldung steht dort, wo gerade gearbeitet wird: bei offener Maske IM Panel
          (siehe unten), sonst hier. Hinter dem Schleier wäre sie ein Alarm, auf den
          niemand reagieren kann — und die Eingabe, um die es geht, läge davor. */}
      {!formularOffen && meldung !== null && <Hinweis art="fehler">{meldung}</Hinweis>}
      {ladeFehler !== null && <Hinweis art="fehler">{ladeFehler}</Hinweis>}

      {neuOffen && (
        <Modal titel="Neuer Ansprechpartner" breite="mittel"
          onAbbrechen={formularAbbrechen} abbrechenGesperrt={speichert}>
          {kontaktFormular.meldung !== null && (
            <Hinweis art="fehler">{kontaktFormular.meldung}</Hinweis>)}
          <Formular beschriftung="oben" {...kontaktFormular.formularProps}>
            {felderBlock("kap-neu")}
          </Formular>
          <ModalFuss speichernText="Anlegen" speichernGesperrt={speichert}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void beiAnlegen(); }} onAbbrechen={formularAbbrechen} />
        </Modal>
      )}

      {kontakte === null && ladeFehler === null && <p className="kap__hinweis">Lädt …</p>}

      {kontakte !== null && kontakte.length === 0 && (
        <p className="kap__leer">
          Für diesen Kunden ist noch kein Ansprechpartner erfasst. Der erste wird zugleich
          Hauptansprechpartner.
        </p>
      )}

      {kontakte !== null && kontakte.length > 0 && (
        <ul className="kap__liste">
          {kontakte.map((k) => {
            const ausgeschieden = k.status === "LEFT";
            const inBearbeitung = bearbeitet === k.id;
            // Ein GEERBTER Ansprechpartner (Etappe 2) ist an dieser Akte sichtbar und nicht
            // änderbar — er trägt seine Herkunft statt der Bearbeitungsknöpfe.
            const geerbt = k.inherited === true;
            return (
              <li key={k.id}
                className={ausgeschieden ? "kap__eintrag kap__eintrag--weg" : "kap__eintrag"}>
                <div className="kap__zeile">
                  {k.istHauptansprechpartner && (
                    <span className="kap__stern" role="img" aria-label="Hauptansprechpartner">
                      ★
                    </span>
                  )}
                  <span className="kap__name">{k.anzeigename}</span>
                  {k.jobTitle !== null && k.jobTitle !== "" && (
                    <span className="kap__funktion">{k.jobTitle}</span>
                  )}
                </div>

                <div className="kap__wege">
                  {/* Die ERSTE E-Mail/Telefonnummer steht weiter hier — die kurze
                      Auskunft, für die niemand klicken soll. Sie kommt seit Aufgabe 4 aus
                      der Hauptadresse (die API leitet email/phone daraus ab), nicht mehr
                      aus einer eigenen Spalte. Mehr als eine je Art zeigt erst der
                      aufklappbare Abschnitt darunter. */}
                  {k.email !== null && k.email !== "" && (
                    <span className="kap__weg">{k.email}</span>
                  )}
                  {k.phone !== null && k.phone !== "" && (
                    <span className="kap__weg">{k.phone}</span>
                  )}
                  {/* Ein geerbter Ansprechpartner gehört einem anderen Kunden — seine
                      Kontaktwege lassen sich über DIESE Akte weder lesen noch ändern
                      (die Route findet die Verknüpfung an diesem Kunden nicht). */}
                  {/* Aufklapper: me-knopf mit `ausgeklappt` (aria-expanded am inneren Knopf,
                      me-design 8cc13b0, P4-2). */}
                  {!geerbt && (
                    <KiKnopf ausgeklappt={wegeFuer === k.id}
                      name={`Kontaktwege von ${k.anzeigename}`}
                      onClick={() => setWegeFuer((offen) => (offen === k.id ? null : k.id))}>
                      {wegeFuer === k.id ? "Kontaktwege schliessen" : "Kontaktwege"}
                    </KiKnopf>
                  )}
                </div>

                {geerbt && (
                  <div className="kap__herkunft">
                    {onKunde !== undefined ? (
                      <KiKnopf art="leise" className="kap__herkunft-verweis"
                        name={`${k.originCustomerName}, anzeigen`}
                        onClick={() => onKunde(k.originCustomerId)}>
                        von {k.originCustomerName}
                      </KiKnopf>
                    ) : (
                      <span className="kap__herkunft-text">
                        von {k.originCustomerName}
                      </span>
                    )}
                  </div>
                )}

                {wegeFuer === k.id && (
                  <div className="kap__wege-panel">
                    {/* `key` am OBJEKT UND an `wegeVersion`: Ohne den ersten nähme ein
                        Wechsel des aufgeklappten Ansprechpartners den inneren Zustand des
                        Bausteins (Panel, Entwurf, Meldung) auf den nächsten mit; ohne den
                        zweiten zeigte die Liste nach einem Stammdaten-Speichern (das auf
                        die Hauptadresse durchschreibt) ihren alten Bestand weiter, weil
                        sich weder `client` noch `ownerId` dabei ändern. */}
                    <AdressListe key={`${k.id}-${wegeVersion}`} client={p.client}
                      besitzer="contacts" ownerId={k.id} titel="Kontaktwege"
                      namensVorschlag={k.anzeigename}
                      verdeckt={formularOffen || arbeitet}
                      onGeaendert={() => { void neuLaden(); }} />
                  </div>
                )}

                {k.roles.length > 0 && (
                  <div className="kap__rollen">
                    {k.roles.map((rolle) => (
                      <span key={rolle} className="kap__rolle">{rolle}</span>
                    ))}
                  </div>
                )}

                {ausgeschieden && (
                  <p className="kap__weg-datum">
                    {k.leftOn !== null
                      ? `ausgeschieden ${formatDatum(k.leftOn)}`
                      : "ausgeschieden"}
                  </p>
                )}

                {/* Der WARTEZUSTAND: Die Sperre liegt bei jemand anderem (oder wird gerade
                    erworben). Es steht kein Panel, also steht die Leiste hier — mit dem
                    Band, „Übernehmen" und dem Rückzug „Nicht mehr warten". Sobald die
                    Bearbeitung wirklich läuft, wandert sie ins Panel (siehe unten). */}
                {/* Ein geerbter Ansprechpartner hat weder Sperre noch Knöpfe — der
                    Verweis oben ist der einzige Weg, den diese Zeile anbietet (Spec: „nur
                    am Ursprungskunden bearbeitbar"). Die Sperre auf einem fremden Kunden zu
                    erwerben wäre nicht nur nutzlos, sondern hielte dessen Bearbeiter auf. */}
                {geerbt ? null : (
                  wunsch === k.id && !inBearbeitung ? (
                    <div className="kap__bearbeitung">
                      <BearbeitenLeiste
                        modus="LESEN"
                        sperre={zustand}
                        darfUebernehmen={p.darfUebernehmen}
                        onBearbeiten={() => beiBearbeiten(k)}
                        onSpeichern={() => { void beiSpeichern(); }}
                        onVerwerfen={formularSchliessen}
                        onUebernehmen={() => { void uebernehmen(); }}
                        onNichtMehrWarten={beiNichtMehrWarten}
                      />
                    </div>
                  ) : (
                    <div className="kap__aktionen">
                      <KiKnopf name={`${k.anzeigename} bearbeiten`}
                        deaktiviert={formularOffen || arbeitet}
                        onClick={() => beiBearbeiten(k)}>
                        Bearbeiten
                      </KiKnopf>
                      {/* Stern und Ausscheiden gibt es nur an AKTIVEN Personen: Wer
                          ausgeschieden ist, soll nicht Hauptansprechpartner werden können —
                          genau der Zustand, den die Ausscheide-Nachfrage verhindern soll. */}
                      {!ausgeschieden && !k.istHauptansprechpartner && (
                        <KiKnopf name={`${k.anzeigename} zum Hauptansprechpartner machen`}
                          deaktiviert={formularOffen || arbeitet}
                          onClick={() => setSternFrage(k)}>
                          Zum Hauptansprechpartner machen
                        </KiKnopf>
                      )}
                      {!ausgeschieden && (
                        <KiKnopf name={`${k.anzeigename} ausscheiden lassen`}
                          deaktiviert={formularOffen || arbeitet}
                          onClick={() => {
                            ausscheidenFormular.zuruecksetzen();
                            setMeldung(null);
                            setAusscheiden({
                              kontakt: k, leftOn: heuteAlsTag(), wahl: "OFFEN",
                              nachfolgerId: "",
                            });
                          }}>
                          Ausscheiden
                        </KiKnopf>
                      )}
                    </div>
                  )
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Die BEARBEITUNG einer Person. Ein Panel für alle Zeilen und nicht eines je Zeile:
          Es kann nur eine Person zugleich bearbeitet werden (`bearbeitet` hält genau eine
          Kennung), und `stand` IST der Satz, auf dem der Entwurf beruht — dieselbe Quelle,
          aus der auch die Version beim Speichern kommt. */}
      {bearbeitet !== null && stand !== null && (
        <Modal titel={`${stand.anzeigename} bearbeiten`} breite="mittel"
          onAbbrechen={formularAbbrechen} abbrechenGesperrt={speichert}>
          {/* Die Leiste trägt hier NUR noch ihr Störungsband (ein fehlgeschlagener
              Herzschlag) — die zwei Ausgänge liegen in der Fußleiste des Panels. */}
          <BearbeitenLeiste
            modus={speichert ? "SPEICHERT" : "BEARBEITEN"}
            sperre={zustand}
            darfUebernehmen={p.darfUebernehmen}
            aktionenImPanel
            onBearbeiten={() => { /* im Panel gibt es kein zweites Öffnen */ }}
            onSpeichern={() => { void beiSpeichern(); }}
            onVerwerfen={formularSchliessen}
            onUebernehmen={() => { void uebernehmen(); }}
            onNichtMehrWarten={beiNichtMehrWarten}
          />
          {kontaktFormular.meldung !== null && (
            <Hinweis art="fehler">{kontaktFormular.meldung}</Hinweis>)}
          <Formular beschriftung="oben" {...kontaktFormular.formularProps}
            hinweis={konflikt === null ? undefined : (
              <span>
                {konflikt.von !== ""
                  ? <>{konflikt.von} hat diesen Ansprechpartner inzwischen geändert
                      ({formatKonfliktZeit(konflikt.wann)}).</>
                  : <>Dieser Ansprechpartner wurde inzwischen geändert.</>}
                {" "}Deine Eingaben sind nicht verloren — entscheide, welcher Stand gilt.
                {konflikt.fremd !== null && (
                  <> Aktueller Stand: {konflikt.fremd.anzeigename}
                    {konflikt.fremd.jobTitle !== null ? `, ${konflikt.fremd.jobTitle}` : ""}.</>
                )}
                {" "}„Neu laden“ übernimmt ihn.{" "}
                <KiKnopf className="ki-eingaben-behalten"
                  onClick={eigeneEingabenBehalten}>
                  Meine Eingaben behalten
                </KiKnopf>
              </span>
            )}>
            {felderBlock("kap-bearbeiten")}
          </Formular>
          {/* SPEICHERN IST GESPERRT, SOLANGE DIE SPERRE FREMD IST (Ruling zur Fixrunde 1,
              07.09.2026) — dieselbe Verschärfung wie an den Stammdaten und aus demselben
              Grund: Entscheidung AA bleibt gültig (der Versionsvergleich schützt, nicht die
              Sperre), aber neben dem FREMD-Band gehört kein scharfer Speichern-Knopf. Wer
              trotzdem muss, übernimmt die Sperre — oder bricht ab. */}
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={kontaktFormular.zustand !== "bereit" || zustand.art === "FREMD"}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void beiSpeichern(); }} onAbbrechen={formularAbbrechen} />
        </Modal>
      )}

      {sternFrage !== null && (
        <Modal titel="Hauptansprechpartner wechseln" abbrechenGesperrt={arbeitet}
          onAbbrechen={() => { if (!arbeitet) setSternFrage(null); }}>
          <p className="kap__dialog-text">
            {bisherigerHaupt !== null && bisherigerHaupt.id !== sternFrage.id
              ? `${sternFrage.anzeigename} wird Hauptansprechpartner — `
                + `${bisherigerHaupt.anzeigename} verliert den Stern.`
              : `${sternFrage.anzeigename} wird Hauptansprechpartner.`}
          </p>
          {/* Eine Rückfrage OHNE eigenen Entwurf: Es gibt nichts zu verlieren, also fragt
              das Abbrechen nicht noch einmal nach. Der Riegel während des Umhängens bleibt
              — er schützt nicht Eingaben, sondern die laufende Antwort. */}
          <ModalFuss speichernText="Umhängen" speichernGesperrt={arbeitet}
            abbrechenGesperrt={arbeitet}
            onSpeichern={() => { void sternUmhaengen(); }}
            onAbbrechen={() => { if (!arbeitet) setSternFrage(null); }} />
        </Modal>
      )}

      {ausscheiden !== null && (
        <Modal titel={`${ausscheiden.kontakt.anzeigename} scheidet aus`}
          abbrechenGesperrt={ausscheidenFormular.laeuft} onAbbrechen={ausscheidenAbbrechen}>
          {ausscheidenFormular.meldung !== null && (
            <Hinweis art="fehler">{ausscheidenFormular.meldung}</Hinweis>)}
          {/* Eigener Text statt des Vorgabetexts von me-formular (Sie-Form): Hier gibt es
              keinen Serverstand, der Eingaben ersetzen könnte — Datum und Wahl sind
              Entscheidungen dieser Maske. „Neu laden“ holt nur den frischen Satz und damit
              die neue Version (Fixrunde 1, H3). */}
          <Formular beschriftung="oben" {...ausscheidenFormular.formularProps}
            hinweis={ausscheidenFormular.zustand === "fremder-stand" ? (
              <span>
                {ausscheiden.kontakt.anzeigename} wurde zwischenzeitlich geändert. „Neu laden“
                holt den aktuellen Stand; dein Datum und deine Wahl bleiben stehen — danach
                kannst du erneut ausscheiden lassen.
              </span>
            ) : undefined}>
            <Feld id="kap-leftOn" label="Ausgeschieden am" typ="date" bearbeitbar
              wert={ausscheiden.leftOn}
              onChange={(wert) => setAusscheiden((d) => (d === null ? d : { ...d, leftOn: wert }))}
              {...(feldFehler.leftOn !== undefined ? { fehler: feldFehler.leftOn } : {})}
            />

            {ausscheiden.kontakt.istHauptansprechpartner && (
              <fieldset className="kap__nachfolge">
                <legend className="kap__nachfolge-titel">
                  {ausscheiden.kontakt.anzeigename} ist Hauptansprechpartner — wer folgt nach?
                </legend>
                <p className="kap__hinweis">
                  Ohne Entscheidung bliebe ein ausgeschiedener Mensch Hauptansprechpartner.
                  Deshalb ist eine der beiden Antworten nötig.
                </p>
                <label className="kap__nachfolge-eintrag">
                  <input type="radio" name="kap-nachfolge" checked={ausscheiden.wahl === "NACHFOLGER"}
                    disabled={nachfolgeKandidaten(aktive, ausscheiden.kontakt.id).length === 0}
                    onChange={() => {
                      setAusscheiden((d) => (d === null ? d : { ...d, wahl: "NACHFOLGER" }));
                      ausscheidenFormular.feldFehlerRaeumen("nachfolger");
                    }}
                  />
                  <span>Nachfolger benennen</span>
                </label>
                {/* Gesperrt (nicht abgeschaltet), solange nicht „Nachfolger benennen“
                    gewählt ist — lesbar bleibt sie. */}
                <Auswahl id="kap-nachfolger" name="kap-nachfolger" className="kap__nachfolge-auswahl"
                  beschriftung="Nachfolger" wert={ausscheiden.nachfolgerId}
                  gesperrt={ausscheiden.wahl !== "NACHFOLGER"}
                  onGeaendert={(e) => {
                    setAusscheiden((d) => (d === null ? d : { ...d, nachfolgerId: e.wert }));
                    ausscheidenFormular.feldFehlerRaeumen("nachfolger");
                  }}>
                  <option value="">Bitte wählen …</option>
                  {nachfolgeKandidaten(aktive, ausscheiden.kontakt.id).map((k) => (
                    <option key={k.id} value={k.id}>{k.anzeigename}</option>
                  ))}
                </Auswahl>
                <label className="kap__nachfolge-eintrag">
                  <input type="radio" name="kap-nachfolge" checked={ausscheiden.wahl === "OHNE"}
                    onChange={() => {
                      setAusscheiden((d) => (d === null ? d
                        : { ...d, wahl: "OHNE", nachfolgerId: "" }));
                      ausscheidenFormular.feldFehlerRaeumen("nachfolger");
                    }}
                  />
                  <span>Vorerst ohne Hauptansprechpartner</span>
                </label>
                {feldFehler.nachfolger !== undefined && (
                  <span className="kap__feldfehler">{feldFehler.nachfolger}</span>
                )}
              </fieldset>
            )}
          </Formular>

          <ModalFuss speichernText="Ausscheiden lassen"
            speichernGesperrt={ausscheidenFormular.zustand !== "bereit"
              || !entscheidungGetroffen(ausscheiden)}
            abbrechenGesperrt={ausscheidenFormular.laeuft}
            onSpeichern={() => { void ausscheidenAbschicken(); }}
            onAbbrechen={ausscheidenAbbrechen} />
        </Modal>
      )}
      {bestaetigung}
    </div>
  );
}

/** Die möglichen Nachfolger: alle AKTIVEN ausser dem Ausscheidenden selbst. */
function nachfolgeKandidaten(
  aktive: Ansprechpartner[], ausserId: string,
): Ansprechpartner[] {
  return aktive.filter((k) => k.id !== ausserId);
}
